import React from 'react'
import '../../css/rbac/RbacUsersTable.scss'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Skeleton from '@mui/material/Skeleton'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import {
  ArrowsLeftRight,
  ListMagnifyingGlass,
  PencilSimpleLine,
  Prohibit,
  UserCircle
} from '@phosphor-icons/react'
import dayjs from '../../utils/dayjsConfig'
import DataTable from '../../components/table/DataTable'
import UbiquaTablePagination from '../../components/table/UbiquaTablePagination'
import RowActionsMenu from '../../components/table/RowActionsMenu'
import { RbacRoleChip, RbacStatusChip } from './RbacChips'
import {
  rbacSessionsCountSx,
  rbacSessionsMetaSx,
  rbacTableSkeletonBarSx
} from './constants/rbacTableConstants'

const skeletonBarSx = rbacTableSkeletonBarSx

const sensitiveChipSx = {
  height: 24,
  borderRadius: '12px',
  fontSize: '11.5px',
  fontWeight: 600,
  letterSpacing: '0.1px'
}

const roleChipLinkSx = {
  display: 'inline-flex',
  textDecoration: 'none',
  color: 'inherit',
  '&:hover, &:focus, &:active': {
    textDecoration: 'none',
    color: 'inherit'
  }
}

const SKELETON_COLUMN_WIDTHS = [
  { code: '58%', name: '72%', email: '88%', role: 96, sessions: '64%', status: 72 },
  { code: '46%', name: '84%', email: '76%', role: 112, sessions: '52%', status: 80 },
  { code: '64%', name: '68%', email: '92%', role: 88, sessions: '70%', status: 68 },
  { code: '52%', name: '78%', email: '70%', role: 104, sessions: '58%', status: 76 }
]

function formatLastSession(value) {
  const date = dayjs(value)
  if (!date.isValid()) return '—'

  return date.isSame(dayjs(), 'day') ? date.format('HH:mm:ss') : date.format('DD/MM/YYYY')
}

function formatLastSessionTitle(value) {
  const date = dayjs(value)
  return date.isValid() ? date.format('DD/MM/YYYY, HH:mm:ss') : ''
}

function UsersTableSkeletonRows({ compact = false, rowCount = 10 }) {
  const count = Math.max(Number(rowCount) || 10, 1)

  return Array.from({ length: count }, (_, index) => {
    const widths = SKELETON_COLUMN_WIDTHS[index % SKELETON_COLUMN_WIDTHS.length]

    return (
      <TableRow
        key={`users-skeleton-${index}`}
        hover={false}
        sx={{
          cursor: 'default',
          pointerEvents: 'none',
          '&:hover': {
            backgroundColor: 'transparent'
          }
        }}
      >
        <TableCell>
          <Skeleton
            animation="wave"
            variant="rounded"
            height={16}
            width={widths.code}
            sx={skeletonBarSx}
          />
        </TableCell>
        <TableCell>
          <Skeleton
            animation="wave"
            variant="rounded"
            height={16}
            width={widths.name}
            sx={skeletonBarSx}
          />
        </TableCell>
        <TableCell>
          <Skeleton
            animation="wave"
            variant="rounded"
            height={16}
            width={widths.email}
            sx={skeletonBarSx}
          />
        </TableCell>
        {!compact ? (
          <>
            <TableCell>
              <Skeleton
                animation="wave"
                variant="rounded"
                height={24}
                width={widths.role}
                sx={{ ...skeletonBarSx, borderRadius: '12px' }}
              />
            </TableCell>
            <TableCell>
              <Skeleton
                animation="wave"
                variant="rounded"
                height={16}
                width={widths.sessions}
                sx={skeletonBarSx}
              />
              <Skeleton
                animation="wave"
                variant="rounded"
                height={12}
                width="46%"
                sx={{ ...skeletonBarSx, mt: 0.75 }}
              />
            </TableCell>
          </>
        ) : null}
        <TableCell align="center">
          <Skeleton
            animation="wave"
            variant="rounded"
            height={24}
            width={widths.status}
            sx={{ ...skeletonBarSx, borderRadius: '12px', mx: 'auto' }}
          />
        </TableCell>
        <TableCell align="center">
          <Skeleton
            animation="wave"
            variant="circular"
            width={28}
            height={28}
            sx={{ ...skeletonBarSx, mx: 'auto' }}
          />
        </TableCell>
      </TableRow>
    )
  })
}

function UsersEmptyState({ title, description }) {
  return { title, description, icon: <ListMagnifyingGlass size={34} weight="regular" /> }
}

function userStatusMeta(user) {
  if (user.status === 'inactive') return { label: 'INACTIVO' }
  if (user.status === 'pending' || (!user.active && user.email_verified === false)) {
    return { label: 'PENDIENTE' }
  }
  if (user.active || user.status === 'active') return { label: 'ACTIVO' }
  return { label: 'INACTIVO' }
}

export default function RbacUsersTable({
  loading,
  paginatedUsers,
  pagination,
  isAdmin,
  actionMenuAnchorEl,
  actionMenuUser,
  onUserRowClick,
  onOpenActionMenu,
  onCloseActionMenu,
  onActionMenuExited,
  onEditAction,
  onAssignAction,
  onViewAction,
  onStatusAction,
  onPageChange,
  onRowsPerPageChange,
  actionMode = 'manage',
  canReassign = true,
  fallbackRole = null,
  emptyState: emptyStateOverride = null,
  header = null
}) {
  const isRoleUsersMode = actionMode === 'role-users'

  const baseColumns = [
    {
      key: 'code',
      label: 'CODIGO',
      width: isRoleUsersMode ? '13%' : '9%',
      truncate: true,
      getTitle: (user) => user.code || '',
      render: (user) => <span className="ubiqua-identity-separate__code">{user.code || '—'}</span>
    },
    {
      key: 'name',
      label: 'NOMBRE',
      width: isRoleUsersMode ? '28%' : '18%',
      truncate: true,
      getTitle: (user) => user.name || '',
      render: (user) => <span className="ubiqua-identity-separate__name">{user.name || '—'}</span>
    },
    {
      key: 'email',
      label: 'CORREO ELECTRONICO',
      width: isRoleUsersMode ? '43%' : '23%',
      truncate: true,
      getTitle: (user) => user.email || '',
      render: (user) => user.email
    }
  ]

  const indexColumns = [
    {
      key: 'roles',
      label: 'ROL ASIGNADO',
      width: '20%',
      render: (user) => {
        const userRoles = user.roles?.length > 0 ? user.roles : fallbackRole ? [fallbackRole] : []

        return (
          <>
            {userRoles.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {userRoles.map((role) => (
                  <Box
                    key={role.id}
                    component={RouterLink}
                    to={`/configuracion/rbac/roles/${role.id}`}
                    onClick={(event) => event.stopPropagation()}
                    sx={roleChipLinkSx}
                  >
                    <RbacRoleChip label={role.name} />
                  </Box>
                ))}
              </Box>
            ) : (
              <RbacRoleChip label="SIN ROL" unassigned />
            )}
            {isAdmin && user.can_manage_rbac ? (
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                <Chip label="Gestiona RBAC" size="small" variant="outlined" sx={sensitiveChipSx} />
              </Box>
            ) : null}
          </>
        )
      }
    },
    {
      key: 'sessions',
      label: 'SESIONES',
      width: '12%',
      render: (user) => (
        <>
          <Typography variant="body2" sx={rbacSessionsCountSx}>
            {user.active_sessions_count ?? 0} sesione(s)
          </Typography>
          {user.last_session_at ? (
            <Typography
              variant="caption"
              display="block"
              title={formatLastSessionTitle(user.last_session_at)}
              sx={rbacSessionsMetaSx}
            >
              Última: {formatLastSession(user.last_session_at)}
            </Typography>
          ) : null}
        </>
      )
    }
  ]

  const statusColumn = {
    key: 'status',
    label: 'ESTADO',
    width: isRoleUsersMode ? '10%' : '9%',
    align: 'center',
    headerAlign: 'left',
    render: (user) => <RbacStatusChip label={userStatusMeta(user).label} />
  }

  const columns = [...baseColumns, ...(isRoleUsersMode ? [] : indexColumns), statusColumn]

  const defaultEmptyState = UsersEmptyState({
    title: 'No hay usuarios para mostrar',
    description:
      'No encontramos usuarios con los filtros actuales. Prueba ajustando la busqueda o restableciendo los filtros.'
  })
  const emptyState = emptyStateOverride
    ? {
        ...defaultEmptyState,
        ...emptyStateOverride,
        icon: emptyStateOverride.icon || defaultEmptyState.icon
      }
    : defaultEmptyState

  const primaryActions = isRoleUsersMode
    ? [
        canReassign
          ? {
              key: 'assign',
              label: 'Reasignar usuario',
              icon: <ArrowsLeftRight size={18} weight="regular" />,
              onClick: onAssignAction
            }
          : null,
        {
          key: 'view',
          label: 'Ver usuario',
          icon: <UserCircle size={18} weight="regular" />,
          onClick: onViewAction
        }
      ].filter(Boolean)
    : [
        {
          key: 'edit',
          label: 'Editar usuario',
          icon: <PencilSimpleLine size={18} weight="regular" />,
          onClick: onEditAction
        },
        {
          key: 'assign',
          label: 'Reasignar roles',
          icon: <ArrowsLeftRight size={18} weight="regular" />,
          onClick: onAssignAction
        }
      ]

  const secondaryActions = isRoleUsersMode
    ? []
    : [
        {
          key: 'status',
          label: actionMenuUser?.status === 'inactive' ? 'Activar usuario' : 'Inactivar usuario',
          icon: <Prohibit size={18} weight="regular" />,
          onClick: onStatusAction
        }
      ]

  return (
    <Box>
      <Box className="rbac-users-table-card">
        <DataTable
          ariaLabel="Listado de usuarios RBAC"
          columns={columns}
          rows={paginatedUsers}
          rowKey="id"
          loading={loading}
          renderSkeletonRows={() => (
            <UsersTableSkeletonRows compact={isRoleUsersMode} rowCount={pagination.per_page} />
          )}
          emptyState={emptyState}
          onRowClick={onUserRowClick ? (user) => onUserRowClick(user.id) : undefined}
          header={header}
          actionsEnabled
          onOpenRowActions={onOpenActionMenu}
        />
      </Box>

      <UbiquaTablePagination
        ariaLabel="Paginación de usuarios Torre"
        pagination={pagination}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        itemLabel="usuarios"
        emptyLabel="Sin usuarios para mostrar"
        disabled={loading}
      />

      <RowActionsMenu
        anchorEl={actionMenuAnchorEl}
        currentItem={actionMenuUser}
        onClose={onCloseActionMenu}
        onExited={onActionMenuExited}
        primaryActions={primaryActions}
        secondaryActions={secondaryActions}
      />
    </Box>
  )
}
